import React from 'react'
import './ListPageTable.css'
import {
  formatLongDate,
  formatTableDate,
  toValidDate,
} from '../../utils/formatDate'

export const DETAIL_COLORS = {
  ink: '#061524',
  text: '#0B1E2D',
  muted: '#5F7484',
  muted2: '#8A9BA7',
  line: '#DFE7E8',
  lineSoft: '#EAF0F0',
  surface: '#FFFFFF',
  surface2: '#F7FAFA',
  teal: '#0D9B87',
  tealDeep: '#076B5C',
  tealSoft: '#E1F4F0',
  brand: '#00146D',
  coral: '#D14A3E',
  amber: '#C98A16',
} as const

export const PRIORITY_PILL: Record<
  number,
  { bg: string; color: string; label: string }
> = {
  1: { bg: '#FAE6E3', color: '#96271B', label: 'Critical' },
  2: { bg: '#FAEFDA', color: '#84540A', label: 'High' },
  3: { bg: '#E6EFF8', color: '#22548A', label: 'Medium' },
  4: { bg: '#E1F4F0', color: '#076B5C', label: 'Low' },
}

/** Risk treatment horizon (Short / Medium / Long term) — not task criticality. */
export const RISK_HORIZON_PILL: Record<number, { bg: string; color: string }> =
  {
    1: { bg: '#FAE6E3', color: '#96271B' },
    2: { bg: '#E6EFF8', color: '#22548A' },
    3: { bg: '#EAF0F0', color: '#5F7484' },
  }

export const STATUS_PILL: Record<string, { bg: string; color: string }> = {
  'A faire': { bg: '#E1F4F0', color: '#076B5C' },
  'En cours': { bg: '#E6EFF8', color: '#22548A' },
  Terminé: { bg: '#E1F4F0', color: '#076B5C' },
  Suspendu: { bg: '#EAF0F0', color: '#5F7484' },
  'En attente': { bg: '#FAEFDA', color: '#84540A' },
}

export const MATURITY_PILL: Record<string, { bg: string; color: string }> = {
  '1': { bg: '#EDE7F6', color: '#5E35B1' },
  '2': { bg: '#E6EFF8', color: '#22548A' },
  '3': { bg: '#E1F4F0', color: '#076B5C' },
  '4': { bg: '#FAEFDA', color: '#84540A' },
  '5': { bg: '#FAE6E3', color: '#96271B' },
  Initiation: { bg: '#EDE7F6', color: '#5E35B1' },
  Definition: { bg: '#E6EFF8', color: '#22548A' },
  Définition: { bg: '#E6EFF8', color: '#22548A' },
  Integration: { bg: '#E1F4F0', color: '#076B5C' },
  Intégration: { bg: '#E1F4F0', color: '#076B5C' },
  Management: { bg: '#FAEFDA', color: '#84540A' },
  Optimization: { bg: '#FAE6E3', color: '#96271B' },
  Optimisation: { bg: '#FAE6E3', color: '#96271B' },
}

type PillProps = {
  label: string
  bg: string
  color: string
  showDot?: boolean
  size?: 'md' | 'sm'
  className?: string
}

export function ListPagePill({
  label,
  bg,
  color,
  showDot = true,
  size = 'md',
  className = '',
}: PillProps) {
  return (
    <span
      className={`incyia-pill${size === 'sm' ? ' incyia-pill--sm' : ''} ${className}`.trim()}
      style={{ background: bg, color }}
    >
      {showDot ? <span className="incyia-pill-dot" /> : null}
      {label}
    </span>
  )
}

type DateCellProps = {
  value?: string | Date | null
  locale: string
}

export function ListPageDateCell({ value, locale }: DateCellProps) {
  const d = toValidDate(value)
  if (!d) return <span className="incyia-date-cell is-empty">—</span>
  return (
    <time
      className="incyia-date-cell"
      dateTime={d.toISOString()}
      title={formatLongDate(d, locale)}
    >
      <svg width="14" height="14" viewBox="0 0 16 16" fill="none" aria-hidden>
        <rect
          x="2.5"
          y="3.5"
          width="11"
          height="10"
          rx="1.5"
          stroke="currentColor"
          strokeWidth="1.5"
        />
        <path
          d="M2.5 6.5h11M5.5 2v3M10.5 2v3"
          stroke="currentColor"
          strokeWidth="1.5"
        />
      </svg>
      <span className="incyia-date-text">{formatTableDate(d, locale)}</span>
    </time>
  )
}
